import { Checkbox } from "flowbite-react";
import { Calendar1, Search, X } from "lucide-react";
import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "../../forms/Label";
import { Input } from "../../forms/Input";
import Select, { TSelectOption } from "@/components/forms/Select/Select";
import DatePicker from "react-datepicker";
import { formatLocalDate } from "@/utils";
import { getDateFormatByLocale, safeParseDate } from "@/utils/commonUtils";
import type { TGroupedAttributeDefinitions } from "@/core/types/employee-attributes";
import { getEmployeeAttributeOptions } from "./employeeAttributeUtils";

export type TEmployeeAttributeDropdownProps = {
  searchKey: string;
  setSearchKey: (value: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  defaultSearchKey?: string;
  id?: string;
  employeeAttributeDefinitions?: TGroupedAttributeDefinitions;
};

const EmployeeAttributeDropdown = ({
  searchKey,
  setSearchKey,
  searchValue,
  setSearchValue,
  handleSearch,
  defaultSearchKey = "",
  id = "employee-attributes",
  employeeAttributeDefinitions,
}: TEmployeeAttributeDropdownProps) => {
  const { t } = useTranslation("common");
  const [isFocused, setIsFocused] = useState(false);

  const fields = useMemo(() => {
    return employeeAttributeDefinitions
      ? Object.values(employeeAttributeDefinitions).flat()
      : [];
  }, [employeeAttributeDefinitions]);

  const hasActiveSearch = searchKey && searchValue;
  const currentFieldMeta = useMemo(
    () => fields.find((f) => f.key === searchKey),
    [fields, searchKey],
  );

  const searchKeyOptions: TSelectOption[] = useMemo(
    () =>
      fields
        .filter((field) => field.active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((field) => ({
          value: field.key,
          label: field.label,
        })),
    [fields],
  );

  const selectedSearchKeyOption = useMemo(
    () => searchKeyOptions.find((opt) => opt.value === searchKey) ?? null,
    [searchKeyOptions, searchKey],
  );

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  const handleSearchKeyChange = useCallback(
    (option: TSelectOption | null) => {
      setSearchKey(option?.value ?? "");
      setSearchValue("");
    },
    [setSearchKey, setSearchValue],
  );

  const handleClearSearch = useCallback(() => {
    setSearchKey(defaultSearchKey);
    setSearchValue("");
    handleSearch();
  }, [setSearchKey, setSearchValue, handleSearch, defaultSearchKey]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const renderControl = () => {
    if (!currentFieldMeta) {
      return (
        <div className="relative w-full">
          <Input
            variant="sm"
            type="text"
            placeholder={t("SEARCH")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            disabled
            className="w-full border-0 focus:outline-none focus:ring-0 focus:border-0"
            id={`${id}-disabled-input`}
          />
        </div>
      );
    }

    const { data_type, options, placeholder } = currentFieldMeta;
    const computedPlaceholder = placeholder ?? t("SEARCH");

    switch (data_type) {
      case "date":
      case "datetime":
        return (
          <div className="relative w-full">
            <DatePicker
              id={`${id}-date-search-input`}
              icon={<Calendar1 />}
              showMonthDropdown
              showYearDropdown
              autoComplete="off"
              dropdownMode="select"
              selected={safeParseDate(searchValue)}
              onChange={(date: Date | null) => {
                if (date) {
                  const formattedDate = formatLocalDate(date);
                  setSearchValue(formattedDate);
                } else {
                  setSearchValue("");
                }
              }}
              toggleCalendarOnIconClick
              showIcon
              placeholderText={
                computedPlaceholder || getDateFormatByLocale("single", true)
              }
              dateFormat={getDateFormatByLocale()}
              onKeyDown={(e) => e.preventDefault()}
              className="block w-full dark:[color-scheme:dark] border-0 focus:outline-none focus:ring-0 focus:border-0"
            />
          </div>
        );

      case "bool":
      case "boolean":
        return (
          <div className="flex w-full items-center space-x-4 ml-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${id}-bool-true`}
                checked={searchValue === "True"}
                onChange={() =>
                  setSearchValue(searchValue === "True" ? "" : "True")
                }
              />
              <Label htmlFor={`${id}-bool-true`} weight={"medium"}>
                {t("YES")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${id}-bool-false`}
                checked={searchValue === "False"}
                onChange={() =>
                  setSearchValue(searchValue === "False" ? "" : "False")
                }
              />
              <Label htmlFor={`${id}-bool-false`} weight={"medium"}>
                {t("NO")}
              </Label>
            </div>
          </div>
        );

      case "dropdown":
      case "select":
      case "enum": {
        const mappedOptions = getEmployeeAttributeOptions(options);

        const selectedDropdownOption =
          mappedOptions.find((opt) => opt.value === searchValue) ?? null;

        return (
          <div className="relative w-full">
            <Select
              name="dynamicSelect"
              options={mappedOptions}
              value={selectedDropdownOption}
              onChange={(opt: TSelectOption | null) =>
                setSearchValue(opt?.value ?? "")
              }
              onFocus={handleFocus}
              onBlur={handleBlur}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              placeholder={computedPlaceholder}
              className="w-full text-sm border-none shadow-none"
              isClearable={false}
              isSearchable={false}
            />
          </div>
        );
      }

      case "int":
      case "float":
      case "number":
      case "decimal":
        return (
          <div className="relative w-full">
            <Input
              variant="sm"
              type="number"
              placeholder={computedPlaceholder}
              value={searchValue}
              autoComplete="off"
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full border-0 focus:outline-none focus:ring-0 focus:border-0"
              id={`${id}-number-search-input`}
            />
          </div>
        );

      case "string":
      case "text":
      case "varchar":
      default:
        // Default text fallback for unknown data_type values
        return (
          <div className="relative w-full">
            <Input
              variant="sm"
              type="text"
              placeholder={computedPlaceholder}
              value={searchValue}
              autoComplete="off"
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full border-0 focus:outline-none focus:ring-0 focus:border-0"
              id={`${id}-text-search-input`}
            />
            {!hasActiveSearch && (
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 transform rounded p-1"
                aria-label="search"
                id={`${id}-search-button`}
              >
                <Search className="h-4 w-4 text-foreground" />
              </button>
            )}
          </div>
        );
    }
  };

  return (
    <div className="w-full">
      <div
        className={`relative flex items-center border focus-within:border-primary pr-2 text-foreground w-full search-dropdown ${
          isFocused ? "search-dropdown-focused" : ""
        }`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {/* Select Dropdown with Chevron */}
        <div className="relative flex items-center">
          <Select
            name="searchKey"
            options={searchKeyOptions}
            value={selectedSearchKeyOption}
            onChange={(opt: TSelectOption | null) => handleSearchKeyChange(opt)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder={t("FILTER.SELECT_EMPLOYEE_ATTR")}
            className="text-sm"
            isClearable={false}
            isSearchable
          />
        </div>

        {/* Divider */}
        <div className="mx-2 h-8 w-px search-dropdown-divider" />

        {/* Dynamic Input Control */}
        <div className="relative flex-1">{renderControl()}</div>

        {/* Clear Search Button */}
        {hasActiveSearch && (
          <button
            onClick={handleClearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 transform rounded-lg bg-background p-2 shadow-sm"
            aria-label="clear search"
            title="Clear search"
            style={{ zIndex: 2 }}
            id={`${id}-clear-search-button`}
          >
            <X className="h-4 w-4 text-foreground" />
          </button>
        )}
      </div>
    </div>
  );
};

export default EmployeeAttributeDropdown;
